import {
  FocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { BodySmReg } from '@seed-health/tokens';
import { mqLargeMin } from 'styled/tools/media-queries';
import Icon from 'components/global/Icon/Primary';
import Menu from 'components/global/Menu';
import { LocationContext } from 'context-providers/location-provider';
import { useAvailableMarkets } from 'hooks/use-available-markets';

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-8);
  padding: 0;
  border: none;
  background: transparent;
  /* The global "*" reset assigns every element an explicit color, so
     "inherit" never reaches this button. The footer sets --menu-color for
     its dark surface (defaults to the standard text color). */
  color: var(--menu-color, var(--text-primary));
  cursor: pointer;

  &:hover {
    opacity: var(--hover);
  }
  &:active {
    opacity: var(--active);
  }
  &:focus-visible {
    opacity: var(--focused);
    outline: 3px solid var(--focus-color, currentColor);
    outline-offset: var(--focus-offset, 0.3ch);
  }
`;

const SelectedLabel = styled.span`
  ${BodySmReg}
  /* Own declaration required — the global "p, span" reset hardcodes black,
     which beats the color inherited from the trigger button. */
  color: var(--text-inverse-primary);
`;

const PanelContainer = styled.div`
  position: absolute;
  top: calc(100% + var(--spacing-8));
  /* Mobile: center the panel on the viewport. The footer sets
     --menu-viewport-inset to the trigger's inset from the viewport's left
     edge so the centering can account for it (defaults to 0). */
  left: calc(50vw - var(--menu-viewport-inset, 0px));
  transform: translateX(-50%);

  @media ${mqLargeMin} {
    left: 0;
    transform: none;
  }
`;

export const CountrySelector = () => {
  const { effectiveCountryCode, setCountryOverride } =
    useContext(LocationContext);
  const availableCountries = useAvailableMarkets();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (availableCountries.length < 2) {
    return null;
  }

  // Clamp to a country in the option list — an unsupported override prices
  // as US and would otherwise leave the label with nothing to render.
  const selectedCountry =
    availableCountries.find(
      (country) => country.isoCode === effectiveCountryCode,
    ) ??
    availableCountries.find((country) => country.isoCode === 'US') ??
    availableCountries[0];

  const handleSelect = (value: string) => {
    setCountryOverride(value);
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  // Tabbing (or otherwise moving focus) out of the widget closes the menu,
  // so it can't linger open over surrounding content.
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!wrapperRef.current?.contains(event.relatedTarget as Node)) {
      setIsOpen(false);
    }
  };

  // Menu's rows use a roving tabindex, so arrow keys are how the keyboard
  // reaches non-selected options.
  const handleArrowKeys = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();
    const optionNodes = Array.from(
      wrapperRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ??
        [],
    );
    if (optionNodes.length === 0) {
      return;
    }
    const currentIndex = optionNodes.indexOf(
      document.activeElement as HTMLElement,
    );
    const nextIndex =
      currentIndex === -1
        ? event.key === 'ArrowDown'
          ? 0
          : optionNodes.length - 1
        : Math.min(
            Math.max(currentIndex + (event.key === 'ArrowDown' ? 1 : -1), 0),
            optionNodes.length - 1,
          );
    optionNodes[nextIndex].focus();
  };

  return (
    <Wrapper ref={wrapperRef} onBlur={handleBlur} onKeyDown={handleArrowKeys}>
      <TriggerButton
        ref={buttonRef}
        type='button'
        aria-label='Country and currency'
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <Icon name='globe' size='md' />
        <SelectedLabel>
          {`${selectedCountry.currency.isoCode} / ${selectedCountry.name}`}
        </SelectedLabel>
        <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size='md' />
      </TriggerButton>
      {isOpen ? (
        <PanelContainer>
          <Menu
            aria-label='Country and currency'
            options={availableCountries.map(({ isoCode, name, currency }) => ({
              value: isoCode,
              label: `${currency.isoCode} / ${name}`,
            }))}
            value={selectedCountry.isoCode}
            onChange={handleSelect}
          />
        </PanelContainer>
      ) : null}
    </Wrapper>
  );
};
